import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Chat } from '../types';

interface UseConversationNavigationProps {
  activeChatId: string | null;
  chats: Chat[];
  chatsLoading: boolean;
  chatsError: string | null;
  openedChats: Chat[];
}

interface UseConversationNavigationReturn {
  handleChatNavigation: (chatId: string) => void;
}

export const useConversationNavigation = ({
  activeChatId,
  chats,
  chatsLoading,
  chatsError,
  openedChats,
}: UseConversationNavigationProps): UseConversationNavigationReturn => {
  const navigate = useNavigate();

  // Validate URL chat ID and handle chat selection
  useEffect(() => {
    if (activeChatId) {
      if (chatsLoading) {
        // Still loading chats, wait for them to finish
        return;
      }

      if (chats.length > 0) {
        const selectedChat = chats.find(chat => chat.id === activeChatId);
        if (selectedChat) {
          // Valid chat ID - chat exists in backend
          return;
        } else {
          // Check if this chat is in openedChats (might be newly created)
          const isInOpenedChats = openedChats.some(
            chat => chat.id === activeChatId
          );
          if (!isInOpenedChats) {
            // Invalid chat ID - redirect to conversation home
            console.warn(
              `Chat ID ${activeChatId} not found, redirecting to conversation home`
            );
            navigate('/conversation', { replace: true });
          }
          // If it's in openedChats, let it stay - it's a newly created chat
        }
      } else if (!chatsError) {
        // No chats loaded yet but no error - this might be a valid chat that hasn't loaded
        // Wait for chats to finish loading before making validation decision
        return;
      }
    }
  }, [activeChatId, chats, chatsLoading, chatsError, openedChats, navigate]);

  const handleChatNavigation = (chatId: string): void => {
    navigate(`/conversation/${chatId}`);
  };

  return {
    handleChatNavigation,
  };
};